// packages/web/src/lib/catalog/cache.ts
// Unified caching strategy for catalog detection

import type { CatalogInstance } from './types';

// Cache for catalog instances to avoid repeated API calls
export let catalogCache: Map<string, CatalogInstance> = new Map();
export let cacheTimestamp: number = 0;
export const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache for user catalog preferences
export let userCatalogCache: Map<string, { catalogId: string; timestamp: number }> = new Map();
export const USER_CACHE_DURATION = 30 * 1000; // 30 seconds for user preferences

/**
 * Clear user catalog preference cache (call when user changes preference)
 */
export function clearUserCatalogCache(): void {
  userCatalogCache.clear();

  // Also clear the user active catalog cache
  const activeCacheKey = 'user-active-catalog';
  userCatalogCache.delete(activeCacheKey);
}

/**
 * Check if catalog cache is valid
 */
export function isCatalogCacheValid(cacheKey: string): boolean {
  return Date.now() - cacheTimestamp < CACHE_DURATION && catalogCache.has(cacheKey);
}

/**
 * Get cached catalog instance
 */
export function getCachedCatalog(cacheKey: string): CatalogInstance | undefined {
  return catalogCache.get(cacheKey);
}

/**
 * Cache catalog instance
 */
export function setCachedCatalog(cacheKey: string, catalog: CatalogInstance): void {
  catalogCache.set(cacheKey, catalog);
  cacheTimestamp = Date.now();
}

/**
 * Check if user catalog preference cache is valid
 */
export function isUserCatalogCacheValid(cacheKey: string): boolean {
  const cached = userCatalogCache.get(cacheKey);
  const cacheAge = cached ? Date.now() - cached.timestamp : null;
  return cached && cacheAge !== null && cacheAge < USER_CACHE_DURATION;
}

/**
 * Get cached user catalog preference
 */
export function getCachedUserCatalog(cacheKey: string): string | null {
  const cached = userCatalogCache.get(cacheKey);
  return cached ? cached.catalogId : null;
}

/**
 * Cache user catalog preference
 */
export function setCachedUserCatalog(cacheKey: string, catalogId: string): void {
  userCatalogCache.set(cacheKey, { catalogId, timestamp: Date.now() });
}

/**
 * Clear all catalog caches (call when auth state changes)
 */
export function clearAllCatalogCaches(): void {
  catalogCache.clear();
  cacheTimestamp = 0;

  // Also clear user catalog preferences
  userCatalogCache.clear();
}
